import React from "react";
import { Play, BicepsFlexed, Film } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { coverUrl } from "../lib/api";

const DEFAULT_COVER_MUSIC = "https://images.unsplash.com/photo-1659117675918-69ec794c64f2?crop=entropy&cs=srgb&fm=jpg&w=600&q=80";
const DEFAULT_COVER_SERMON = "https://images.unsplash.com/photo-1650233323933-53e20427e277?crop=entropy&cs=srgb&fm=jpg&w=600&q=80";

export default function TrackCard({ track, index, list, showFavorite = false, onFavoriteToggle, isFavorited }) {
  const { playQueue } = usePlayer();
  const cover = coverUrl(track) || (track.category === "sermon" || track.category === "teaching" || track.category === "study" ? DEFAULT_COVER_SERMON : DEFAULT_COVER_MUSIC);

  const handlePlay = () => {
    if (list && list.length > 0) {
      playQueue(list, index || 0, "normal");
    } else {
      playQueue([track], 0, "normal");
    }
  };

  return (
    <div className="group relative flex flex-col gap-3" data-testid={`track-card-${track.id}`}>
      <div className="aspect-square overflow-hidden rounded-lg border border-[#22262E] bg-[#111317] relative">
        <img src={cover} alt={track.title} className="object-cover w-full h-full hover-lift" loading="lazy" />
        <button
          onClick={handlePlay}
          data-testid={`track-play-${track.id}`}
          className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-[#C5A059] text-[#060709] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 shadow-[0_0_20px_rgba(197,160,89,0.5)]"
          aria-label={`Play ${track.title}`}
        >
          <Play className="w-5 h-5 ml-0.5" strokeWidth={2} />
        </button>
        {track.featured && (
          <div className="absolute top-2 left-2 overline text-[#060709] bg-[#C5A059] px-2 py-0.5 rounded">
            Featured
          </div>
        )}
        {track.media_type === "video" && (
          <div className="absolute top-2 right-2 overline text-[#060709] bg-[#C5A059]/95 px-2 py-0.5 rounded flex items-center gap-1" data-testid={`track-video-badge-${track.id}`}>
            <Film className="w-3 h-3" strokeWidth={2} /> Video
          </div>
        )}
      </div>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[#FDFBF7] font-medium truncate" title={track.title}>{track.title}</div>
          <div className="text-[#A1A4AB] text-sm truncate" title={track.artist}>{track.artist}</div>
          <TrackTaxonomyLine track={track} />
        </div>
        {showFavorite && onFavoriteToggle && (
          <button
            onClick={() => onFavoriteToggle(track.id)}
            className={`p-1.5 rounded-full transition-colors ${isFavorited ? "text-[#C5A059]" : "text-[#6C707A] hover:text-[#FDFBF7]"}`}
            data-testid={`track-fav-${track.id}`}
            aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
          >
            <BicepsFlexed className="w-4 h-4" strokeWidth={1.5} fill={isFavorited ? "#C5A059" : "none"} />
          </button>
        )}
      </div>
    </div>
  );
}

// Inline taxonomy strip — main_genre always visible; long tag list reveals on
// hover/focus/tap. Keeps the card glanceable while still surfacing rich metadata.
function TrackTaxonomyLine({ track }) {
  const main = track.main_genre || track.genre;
  const styles = Array.isArray(track.style_tags) ? track.style_tags : [];
  const moods = Array.isArray(track.mood_tags) ? track.mood_tags : [];
  const allTags = [...styles, ...moods];
  if (!main && allTags.length === 0) return null;
  return (
    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
      {main && (
        <span className="overline text-[10px] text-[#C5A059] truncate max-w-full" data-testid={`track-card-main-genre-${track.id}`}>
          {main}
        </span>
      )}
      {allTags.length > 0 && (
        <span
          className="group/tags relative overline text-[10px] text-[#6C707A] cursor-help"
          tabIndex={0}
          data-testid={`track-card-tags-${track.id}`}
          aria-label={`Tags: ${allTags.join(", ")}`}
        >
          · {allTags.length} tag{allTags.length > 1 ? "s" : ""}
          <span
            role="tooltip"
            className="absolute left-0 top-full mt-1 z-20 hidden group-hover/tags:block group-focus/tags:block group-active/tags:block min-w-[180px] max-w-[260px] p-2 rounded-md border border-[#C5A059]/30 bg-[#0E1014]/95 backdrop-blur-md shadow-[0_0_30px_rgba(0,0,0,0.6)]"
          >
            <span className="flex flex-wrap gap-1">
              {allTags.map((t) => (
                <span key={t} className="px-1.5 py-0.5 rounded-full bg-[#C5A059]/10 border border-[#C5A059]/25 text-[#C5A059] text-[10px] normal-case tracking-normal">
                  {t}
                </span>
              ))}
            </span>
          </span>
        </span>
      )}
    </div>
  );
}
